import { Group } from "@visx/group";
import { Pie } from "@visx/shape";

import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";
import type { SmbQuestionDetails } from "@/web/routes/_protected/smb/-dashboard/-types";

const CHART_WIDTH = 303;
const CHART_HEIGHT = 192;
const PIE_SIZE = 160;
const PIE_RADIUS = PIE_SIZE / 2;
const PIE_CENTER_X = CHART_WIDTH / 2;
const PIE_CENTER_Y = 94;
const MAX_VISIBLE_CATEGORIES = 6;
const SLICE_COLORS = [
  "var(--withus-yellow)",
  "oklch(from var(--withus-blue) calc(l + 0.08) c h)",
  "oklch(from var(--withus-white) calc(l - 0.1) c h)",
  "var(--withus-red)",
  "var(--withus-green)",
  "oklch(from var(--withus-red) calc(l + 0.18) c h)",
];

type QuestionPieChartProps = {
  chart: NonNullable<SmbQuestionDetails["chart"]>;
};

type PieRow = {
  id: string;
  label: string;
  value: number;
};

type ColoredPieRow = PieRow & {
  color: string;
};

function capRows(rows: readonly PieRow[]): PieRow[] {
  if (rows.length <= MAX_VISIBLE_CATEGORIES) return [...rows];

  const sortedByValue = [...rows].sort((a, b) => a.value - b.value);
  const remainingRows = sortedByValue.slice(0, rows.length - (MAX_VISIBLE_CATEGORIES - 1));
  const keptIds = new Set(
    sortedByValue.slice(rows.length - (MAX_VISIBLE_CATEGORIES - 1)).map((row) => row.id),
  );
  const remainingValue = remainingRows.reduce((sum, row) => sum + row.value, 0);
  const cappedRows = rows.filter((row) => keptIds.has(row.id));
  return [...cappedRows, { id: "remaining", label: "Remaining", value: remainingValue }];
}

function sortByValueDesc(rows: readonly PieRow[]): PieRow[] {
  return [...rows].sort((a, b) => b.value - a.value);
}

export function QuestionPieChart({ chart }: QuestionPieChartProps) {
  const rawRows = chart.data.labels.map((label, index) => ({
    id: `${index}-${label}`,
    label,
    value: Math.max(0, chart.data.values[index]?.[0] ?? 0),
  }));
  const rows: ColoredPieRow[] = sortByValueDesc(capRows(rawRows)).map((row, index) => ({
    ...row,
    color: SLICE_COLORS[index % SLICE_COLORS.length] as string,
  }));
  const nonZeroRows = rows.filter((row) => row.value > 0);
  const pieRows = nonZeroRows.length > 0 ? nonZeroRows : rows.map((row) => ({ ...row, value: 1 }));
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const majorityRow = total > 0 ? rows.find((row) => row.value / total > 0.5) : undefined;

  return (
    <figure className="relative mt-5 h-67.5 w-full border border-withus-white text-withus-white">
      {chart.chartLabel ? (
        <figcaption className="absolute top-4 left-4 text-sm font-semibold">
          {chart.chartLabel}
        </figcaption>
      ) : null}
      <svg
        role="img"
        aria-label={chart.chartLabel ? `${chart.chartLabel} pie chart` : "Details pie chart"}
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="absolute inset-x-0 top-6 h-48 w-full overflow-visible"
      >
        <Group top={PIE_CENTER_Y} left={PIE_CENTER_X}>
          <Pie
            data={pieRows}
            pieValue={(row) => row.value}
            outerRadius={PIE_RADIUS}
            innerRadius={0}
            padAngle={0}
          >
            {(pie) =>
              pie.arcs.map((arc) => {
                const isMajority = arc.data.id === majorityRow?.id;
                const [labelX, labelY] = pie.path.centroid(arc);
                const percent = total > 0 ? Math.round((arc.data.value / total) * 100) : 0;

                return (
                  <g key={arc.data.id}>
                    <path d={pie.path(arc) ?? undefined} fill={arc.data.color} />
                    {isMajority ? (
                      <text
                        x={labelX}
                        y={labelY - 3}
                        textAnchor="middle"
                        className="fill-withus-dark-blue text-[0.75rem] font-semibold"
                      >
                        <tspan x={labelX}>{percent}%</tspan>
                        <tspan x={labelX} dy="1.2em">
                          {formatCompactWholeNumberWithUnit(
                            formatNumberToShort(arc.data.value),
                            chart.unit.tag,
                          )}
                        </tspan>
                      </text>
                    ) : null}
                  </g>
                );
              })
            }
          </Pie>
        </Group>
      </svg>
      <div className="absolute inset-x-4 bottom-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        {rows.map((item) => (
          <div key={item.id} className="flex max-w-32 min-w-0 items-center justify-center gap-1.5">
            <span className="size-3 shrink-0" style={{ backgroundColor: item.color }} aria-hidden />
            <span className="truncate text-sm font-semibold" title={item.label}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </figure>
  );
}
